import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'rebass/styled-components';
import pluralize from 'pluralize';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { mapStageToFund } from 'components/organisms/contactDetail/contactInfoWorkDate';
import { humanizeCount } from 'helpers/humanizeCount';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Table, Thead, Row, Cell } from '../companyDataTable';

const SeeMoreLessButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const DATE_FORMAT = 'MMMM D, YYYY';

const FundingTable = ({ organizationStages }) => {
  const [expand, setExpand] = React.useState(false);

  const sortFn = (a, b) => dayjs(b.from).diff(dayjs(a.from));

  const sortedFunding = useMemo(
    () =>
      organizationStages
        .map((round) => ({
          ...round,
          amount: round.moneyRaisedUsd ? Number.parseFloat(round.moneyRaisedUsd) : undefined,
        }))
        .sort(sortFn),
    [organizationStages],
  );

  const numRounds = useMemo(() => organizationStages.length, [organizationStages]);
  const totalAmount = useMemo(
    () => sortedFunding.reduce((acc, round) => acc + (round.amount || 0), 0),
    [sortedFunding],
  );

  return (
    <Box>
      <Text
        fontSize="12px"
        fontWeight="medium"
        marginBottom="8px"
        sx={{ color: 'text.subtle', letterSpacing: '1px', textTransform: 'uppercase' }}
      >
        Funding
      </Text>
      <Text fontSize="14px" fontWeight="medium" marginBottom="8px" color="text.dark">
        {numRounds} {pluralize('Round', numRounds)}{' '}
        {totalAmount > 0 && (
          <>
            for <>{humanizeCount(totalAmount)} USD</>
          </>
        )}
      </Text>
      {expand ? (
        <>
          <SeeMoreLessButton onClick={() => setExpand(false)}>
            See less
            <Box as={ChevronUp} width="16px" height="16px" aria-hidden="true" />
          </SeeMoreLessButton>

          <Table split={false}>
            <Thead>
              <Row>
                <Cell>Series</Cell>
                <Cell>Date</Cell>
                <Cell>Amount</Cell>
              </Row>
            </Thead>
            <tbody>
              {sortedFunding.map((round) => (
                <Row key={round.stage}>
                  <Cell>{mapStageToFund(round.stage) || round.stage}</Cell>
                  <Cell>{round.from && dayjs(round.from).format(DATE_FORMAT)}</Cell>
                  <Cell>{round.moneyRaisedUsd && <>{humanizeCount(round.moneyRaisedUsd)} USD</>}</Cell>
                </Row>
              ))}
            </tbody>
          </Table>
        </>
      ) : (
        <>
          <SeeMoreLessButton onClick={() => setExpand(true)}>
            See more
            <Box as={ChevronDown} width="16px" height="16px" aria-hidden="true" />
          </SeeMoreLessButton>
        </>
      )}
    </Box>
  );
};

FundingTable.propTypes = {
  organizationStages: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default FundingTable;
